import { z } from 'zod';
import { macroInputDeviceSettingsSchema } from '../api/macroInputDevices';
import { sha256HexSchema } from '../api/macroPackage/canonicalJson';
import { macroInputModePolicySchema } from '../api/macroPackage/inputModePolicy';
import { rawInputEventSchema } from '../api/macroPackage/rawInputEvent';
import type { MacroPackageIngestResult } from '../api/macroPackage/publishParity';
import { rawScreenStatesSchema, rawRegionRectSchema } from '../api/macroPackage/rawPackage';
import { canonicalJson } from '../api/macroPackage/canonicalJson';
import { authoringMacroCapabilitiesSchema, captureIntegritySchema, commonMacroCapabilitiesSchema, hostTargetSchema } from '../api/macroPackage/pipelineContracts';
import { AUTHORING_PROFILE, recordingArmSpecSchema, recordingLifecycleSnapshotSchema } from '../api/macroPackage/authoringPrimitives';

const boundedId = z.string().min(1).max(128);

export const MACRO_PACKAGE_CAPTURE_MAX_BATCH = 1_024;

export const macroPackageCaptureMarkSchema = z.object({ screenStateId: z.string().uuid(),
  offsetsUs: z.array(z.number().int().min(-250_000).max(250_000)).min(1).max(64) }).strict()
  .refine(mark => mark.offsetsUs.includes(0), 'mark offsets must include 0');

export const macroPackageCaptureFinalizeSchema = z.object({ campaignId: z.string().min(1).max(128), name: z.string().min(1).max(80),
  editorPacketCount: z.number().int().nonnegative().safe(),
  screenStates: rawScreenStatesSchema,
  regions: z.array(z.object({ regionId: z.string().uuid(), screenStateId: z.string().uuid(), rect: rawRegionRectSchema,
    negativeScreenStateIds: z.array(z.string().uuid()).max(32) }).strict()).max(1_024),
  policy: macroInputModePolicySchema }).strict();

// Reuse the canonical event variants, excluding host-owned clocks/sequence/source and lifecycle markers.
const hostFields = { seq: true, tUs: true, source: true } as const;
export const editorRecordingInputSchema = z.discriminatedUnion('kind', [
  rawInputEventSchema.options[0].omit(hostFields), rawInputEventSchema.options[1].omit(hostFields),
  rawInputEventSchema.options[2].omit(hostFields), rawInputEventSchema.options[3].omit(hostFields),
  rawInputEventSchema.options[4].omit(hostFields), rawInputEventSchema.options[5].omit(hostFields),
  rawInputEventSchema.options[6].omit(hostFields),
]);

/**
 * Editor → host → Playjector. `mark` captures lossless frames around "now"; `finalize` writes
 * `policy.json`, `regions/index.json`, then `manifest.json` (commit marker).
 */
export const macroPackageCaptureRequestSchema = z.object({
  requestId: boundedId, packageId: z.string().uuid(), command: z.enum(['start', 'poll', 'mark', 'finalize', 'abort', 'input', 'shortcut', 'arm', 'disarm', 'toggle']),
  afterSeq: z.number().int().min(-1), captureSource: z.enum(['desktop', 'editor']),
  devices: macroInputDeviceSettingsSchema, editorFocused: z.boolean(),
  mark: macroPackageCaptureMarkSchema.optional(),
  finalize: macroPackageCaptureFinalizeSchema.optional(),
  expectedHost: hostTargetSchema.optional(),
  authoringProfile: z.literal(AUTHORING_PROFILE).optional(), armSpec: recordingArmSpecSchema.optional(),
  toggle: z.object({ operationId: z.string().uuid() }).strict().optional(),
  input: z.object({ sequence: z.number().int().nonnegative().safe(), event: editorRecordingInputSchema }).strict().optional(),
  shortcut: z.object({ enabled: z.boolean() }).strict().optional(),
}).strict()
  .refine(request => (request.command === 'finalize') === (request.finalize !== undefined), 'finalize payload iff finalize')
  .refine(request => (request.command === 'mark') === (request.mark !== undefined), 'mark payload iff mark')
  .refine(request => (request.command === 'input') === (request.input !== undefined), 'input payload iff input')
  .refine(request => (request.command === 'shortcut') === (request.shortcut !== undefined), 'shortcut payload iff shortcut')
  .refine(request => ['start', 'arm'].includes(request.command) === (request.expectedHost !== undefined), 'expectedHost iff start or arm')
  .refine(request => (request.command === 'arm') === (request.armSpec !== undefined), 'armSpec iff arm')
  .refine(request => (request.command === 'toggle') === (request.toggle !== undefined), 'toggle payload iff toggle')
  .refine(request => !['arm', 'disarm', 'toggle'].includes(request.command) || request.authoringProfile === AUTHORING_PROFILE,
    'New recording commands require the explicit authoring profile')
  .refine(request => !request.authoringProfile || !['start', 'mark', 'finalize', 'shortcut'].includes(request.command),
    'Authoring recording uses Host-owned lifecycle, not browser marks/finalization/shortcuts')
  .refine(request => !request.armSpec || request.armSpec.captureSource === request.captureSource, 'Capture source must agree with the arm specification')
  .refine(request => request.command !== 'input' || request.captureSource === 'editor', 'input packets require editor capture')
  .refine(request => request.captureSource !== 'desktop' || !request.finalize || request.finalize.editorPacketCount === 0,
    'Desktop finalization cannot claim editor packets');
export type MacroPackageCaptureRequest = z.infer<typeof macroPackageCaptureRequestSchema>;

/** `more: true` means poll again immediately. The poll queue is a live view; `inputs.jsonl` is the record. */
export const macroPackageCaptureReplySchema = z.object({ requestId: boundedId, packageId: z.string().uuid(),
  success: z.boolean(), events: z.array(rawInputEventSchema).max(MACRO_PACKAGE_CAPTURE_MAX_BATCH), more: z.boolean(),
  stopped: z.boolean(), error: z.string().max(256).optional(), diagnostic: z.string().max(512).optional(),
  host: hostTargetSchema.optional(), capabilities: z.union([commonMacroCapabilitiesSchema, authoringMacroCapabilitiesSchema]).optional(),
  lifecycle: recordingLifecycleSnapshotSchema.optional(),
  appliedInputSequence: z.number().int().nonnegative().safe().optional(), integrity: captureIntegritySchema.optional(),
  shortcut: z.object({ armed: z.boolean(), pressSequence: z.number().int().nonnegative().safe() }).strict().optional(),
  marked: z.object({ screenStateId: z.string().uuid(), tUs: z.number().int().nonnegative(),
    frames: z.array(z.object({ offsetUs: z.number().int(), tUs: z.number().int().nonnegative(),
      file: z.string().max(256) }).strict()).max(64) }).strict().optional(),
  finalized: z.object({ directory: z.string().max(1024), manifestSha256: sha256HexSchema }).strict().optional() }).strict()
  .superRefine((reply, context) => {
    if (reply.finalized && (!reply.success || !reply.integrity || !reply.stopped)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['finalized'], message: 'Finalization requires a sealed complete integrity receipt' });
    }
    if (!reply.success && reply.appliedInputSequence !== undefined) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'Failed application cannot acknowledge input' });
    }
  });
export type MacroPackageCaptureReply = z.infer<typeof macroPackageCaptureReplySchema>;

/** Context-dependent wire invariants cannot be established by parsing either DTO alone. */
export function validateMacroPackageCaptureExchange(request: MacroPackageCaptureRequest,
  reply: MacroPackageCaptureReply): { ok: boolean; issues: string[] } {
  const issues: string[] = [];
  if (request.requestId !== reply.requestId || request.packageId !== reply.packageId) issues.push('capture_reply_identity_mismatch');
  if (reply.success && ['start', 'arm'].includes(request.command) && (!reply.host
    || canonicalJson(reply.host) !== canonicalJson(request.expectedHost ?? null))) issues.push('host_validation_target_changed');
  if (reply.success && request.command === 'input' && reply.appliedInputSequence !== request.input?.sequence) issues.push('recording_integrity_failed');
  if (request.command !== 'input' && reply.appliedInputSequence !== undefined) issues.push('unexpected_input_acknowledgement');
  if (reply.success && request.command === 'shortcut' && !reply.shortcut) issues.push('recording_shortcut_unavailable');
  if (request.command !== 'shortcut' && reply.shortcut !== undefined) issues.push('unexpected_shortcut_acknowledgement');
  if (reply.success && request.command === 'finalize' && (!reply.finalized || !reply.integrity
    || reply.integrity.appliedEditorPackets !== request.finalize?.editorPacketCount)) issues.push('recording_integrity_failed');
  if (request.authoringProfile && (reply.success || reply.lifecycle)) validateAuthoringExchange(request, reply, issues);
  return { ok: issues.length === 0, issues };
}

function validateAuthoringExchange(request: MacroPackageCaptureRequest, reply: MacroPackageCaptureReply, issues: string[]): void {
  if (!reply.capabilities || !authoringMacroCapabilitiesSchema.safeParse(reply.capabilities).success) {
    issues.push('authoring_host_capability_required');
  }
  if (!reply.lifecycle) { issues.push('recording_lifecycle_missing'); return; }
  const state = reply.lifecycle;
  if (state.phase !== 'idle' && state.packageId !== request.packageId) issues.push('recording_lifecycle_identity_mismatch');
  if (reply.stopped !== ['idle', 'finalizing', 'finalized', 'failed'].includes(state.phase)) issues.push('recording_lifecycle_stop_mismatch');
  if (reply.success && request.command === 'arm' && !['warming', 'armed'].includes(state.phase)) issues.push('recording_arm_transition_invalid');
  if (reply.success && request.command === 'disarm' && state.phase !== 'idle') issues.push('recording_disarm_transition_invalid');
  if (state.phase === 'finalized' && (!reply.finalized || !reply.integrity)) issues.push('recording_integrity_failed');
}


export type PlaycastMacroPackageCaptureMessages =
  | { target: 'system'; action: 'macroPackageCapture'; isReply: false; message: MacroPackageCaptureRequest }
  | { target: 'system'; action: 'macroPackageCapture'; isReply: true; message: MacroPackageCaptureReply }
  | { target: 'record'; action: 'packageInputs'; isReply: false; message: MacroPackageCaptureRequest & { localEditor: boolean } }
  | { target: 'record'; action: 'packageInputs'; isReply: true; message: MacroPackageCaptureReply }
  | { target: 'system'; action: 'macroPackageIngest'; isReply: true; message: MacroPackageIngestResult };
